<template>
    <div v-if="org" class="OrgMembers">
        <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold">Členovia</h3>
        </div>
        
        <CCardBox v-if="isLoadingMembers" class="space-y-1">
            <div class="flex items-center gap-2 px-3 pb-2 pt-1 text-sm text-gray-500 dark:text-txt-body">
                <CSpinner size="normal" />
                <span>{{ loadingMessage }}</span>
            </div>
            <div
                v-for="n in skeletonCount"
                :key="n"
                class="flex items-center justify-between gap-3 px-3 py-2 animate-pulse"
            >
                <div class="space-y-1.5">
                    <div class="h-3.5 w-40 rounded bg-gray-200 dark:bg-subtle"></div>
                    <div class="h-2.5 w-28 rounded bg-gray-100 dark:bg-surface-card"></div>
                </div>
                <div class="size-8 rounded-md bg-gray-100 dark:bg-surface-card"></div>
            </div>
        </CCardBox>

        <CCardBox v-else-if="memberDisplays.length > 0" class="divide-y divide-gray-100 dark:divide-default">
            <div
                v-for="member in memberDisplays"
                :key="member.id"
                class="group flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-surface-card/50"
            >
                <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-gray-900 dark:text-txt-body">
                        {{ getMemberName(member) }}
                    </p>
                    <p v-if="getMemberSubLabel(member)" class="truncate text-xs text-gray-500 dark:text-txt-body">
                        {{ getMemberSubLabel(member) }}
                    </p>
                </div>
                <button
                    v-if="memberDisplays.length > 1"
                    type="button"
                    class="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-txt-body transition-colors hover:bg-red-50 hover:text-red-600 hover:dark:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:min-w-0 sm:justify-start dark:hover:bg-red-950/40"
                    :disabled="removingMemberId === member.id"
                    :aria-label="`Odstrániť ${getMemberName(member)} z organizácie`"
                    :title="`Odstrániť ${getMemberName(member)} z organizácie`"
                    @click="onRemoveMember(member)"
                >
                    <CSpinner v-if="removingMemberId === member.id" size="normal" />
                    <CIcon v-else name="trash" size="small" />
                    <span class="hidden sm:inline">Odstrániť</span>
                </button>
            </div>
        </CCardBox>

        <div v-else class="text-sm text-gray-500 dark:text-txt-body py-4">
            Zatiaľ nemá žiadnych členov
        </div>
        
        <CButton
                @click="openInviteModal"
                :size="'small'"
                variant="secondary"
                icon="add"
                class="mt-4"
            >
                Pridať člena
        </CButton>

        <div v-if="org?.memberInvitations?.length" class="mt-4 pt-4 border-t">
            <h4 class="text-sm font-medium text-gray-700 dark:text-txt-body mb-2">Čakajúce pozvánky</h4>
            <div class="space-y-1">
                <div 
                    v-for="invitation in org?.memberInvitations || []" 
                    :key="invitation.email"
                    class="text-sm text-yellow-600 dark:text-yellow-300"
                >
                    {{ invitation.email }}
                    <span v-if="invitation.note" class="text-xs text-gray-600 dark:text-txt-body ml-2">({{ invitation.note }})</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { UserId } from '#shared/types/ids'
import type { NotificationEmail } from '#shared/types/Notifications'
import type { Org } from '#shared/types/Org'
import { getFullName } from '#shared/utils'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useUser } from '@/composables/user/useUser'
import CCardBox from '../ui/CCardBox.vue'
import ConfirmModal from '../modals/ConfirmModal.vue'
import InviteModal from './modals/InviteModal.vue'
import { useModal } from 'vue-final-modal'
import { useToast } from '@/composables/useToast'

const props = defineProps<{
    org: Org
}>()

// Fired after a member is added, invited or removed, so a parent holding its own copy of the
// org (the admin edit page) can reload the member fields.
const emit = defineEmits<{
    updated: []
}>()

const { loadUserProfile } = useUser()
const { loadNotifiableMembersOfOrg, removeMember } = useOrgs()
const { showSuccess, showError } = useToast()

const removingMemberId = ref<UserId | null>(null)
const isLoadingMembers = ref(false)

type OrgMemberDisplay = {
    id: UserId
    firstName?: string
    lastName?: string
    email?: string
}

const memberDisplays = ref<OrgMemberDisplay[]>([])

// Number of placeholder rows to show while profiles load (we know the count up front).
const skeletonCount = computed(() => Math.min(props.org?.memberIds?.length || 1, 8))

// A little personality while the team loads — picked once so it doesn't flicker.
const loadingMessages = [
    'Zháňame všetkých členov dokopy…',
    'Pozeráme sa, kto všetko ťahá za jeden povraz…',
    'Zostavujeme zoznam vášho tímu…',
    'Načítavame členov organizácie…'
]
const loadingMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)]

watch(() => [props.org?.id, props.org?.memberIds] as const, async ([orgId, memberIds]) => {
    if (!memberIds || memberIds.length === 0) {
        memberDisplays.value = []
        isLoadingMembers.value = false
        return
    }

    isLoadingMembers.value = true
    try {
        const notifiableMembers = orgId ? await loadNotifiableMembersOfOrg(orgId) : []
        const emailById = new Map(
            notifiableMembers
                .filter((member): member is NotificationEmail & { id: UserId } => Boolean(member.id))
                .map((member) => [member.id, member.email])
        )

        memberDisplays.value = await Promise.all(
            memberIds.map(async (memberId) => {
                try {
                    const profile = await loadUserProfile(memberId)
                    return {
                        id: memberId,
                        firstName: profile?.firstName,
                        lastName: profile?.lastName,
                        email: emailById.get(memberId)
                    }
                } catch (error) {
                    console.warn(`Failed to load profile for member ${memberId}:`, error)
                    return {
                        id: memberId,
                        firstName: undefined,
                        lastName: undefined,
                        email: emailById.get(memberId)
                    }
                }
            })
        )
    } catch (error) {
        console.error('Failed to load member profiles:', error)
        memberDisplays.value = memberIds.map((memberId) => ({ id: memberId }))
    } finally {
        isLoadingMembers.value = false
    }
}, { immediate: true })


const openInviteModal = async () => {
    try {
        const { open, close } = useModal({
            component: InviteModal,
            attrs: {
                organization: props.org,
                onClose(result?: any) {
                    close()
                    if (result) emit('updated')
                }
            }
        })
        await open()
    } catch (error) {
        console.log('Invite modal error:', error)
    }
}

function getMemberLabel(member: OrgMemberDisplay): string {
    return getFullName(member) || member.email || 'Neznámy člen'
}

// Primary line: full name when known, otherwise email / fallback.
function getMemberName(member: OrgMemberDisplay): string {
    return getMemberLabel(member)
}

// Secondary line: show the email only when it isn't already the primary line.
function getMemberSubLabel(member: OrgMemberDisplay): string {
    const fullName = getFullName(member)
    return fullName && member.email ? member.email : ''
}

async function confirmRemoval(memberLabel: string): Promise<boolean> {
    return await new Promise<boolean>((resolve) => {
        const { open, close } = useModal({
            component: ConfirmModal,
            attrs: {
                title: 'Odstrániť člena',
                message: `Naozaj chcete odstrániť ${memberLabel} z organizácie? Stratí prístup k jej správe.`,
                confirmText: 'Odstrániť',
                cancelText: 'Zrušiť',
                confirmVariant: 'decline',
                onClose(confirmed?: boolean) {
                    close()
                    resolve(confirmed === true)
                }
            }
        })
        open()
    })
}

async function onRemoveMember(member: OrgMemberDisplay): Promise<void> {
    if (removingMemberId.value) return

    const confirmed = await confirmRemoval(getMemberLabel(member))
    if (!confirmed) return

    removingMemberId.value = member.id
    try {
        await removeMember(props.org.id, member.id)
        // Reflect locally so the row disappears immediately. We intentionally don't mutate
        // props.org.memberIds — it's the parent's state (and reassigning it would retrigger
        // the watch above and reload every member profile). The backend is the source of truth.
        memberDisplays.value = memberDisplays.value.filter((m) => m.id !== member.id)
        showSuccess('Člen bol odstránený z organizácie.')
        emit('updated')
    } catch (error: any) {
        console.error('Failed to remove org member:', error)
        showError(error?.message || 'Nepodarilo sa odstrániť člena.')
    } finally {
        removingMemberId.value = null
    }
}
</script>
